import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, KeyRound, Trash2, UserCog } from "lucide-react";

function fmt(d) {
  try { return new Date(d).toLocaleDateString(undefined, { dateStyle: "medium" }); }
  catch { return d || "—"; }
}

export default function AdminFacilitators() {
  const [facilitators, setFacilitators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const reload = () => {
    setLoading(true);
    api.get("/admin/facilitators")
      .then((r) => setFacilitators(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const createFacilitator = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email };
      if (form.password.trim()) payload.password = form.password.trim();
      const r = await api.post("/admin/facilitators", payload);
      toast.success(`Facilitator created — ${r.data.email} · temporary password: ${r.data.temp_password}`, { duration: 9000 });
      setOpen(false);
      setForm({ name: "", email: "", password: "" });
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async (f) => {
    if (!window.confirm(`Reset password for ${f.name}? A new temporary password will be generated.`)) return;
    try {
      const r = await api.post(`/admin/facilitators/${f.id}/reset-password`, {});
      toast.success(`New temporary password for ${f.name}: ${r.data.temp_password}`, { duration: 9000 });
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteFacilitator = async (f) => {
    if (!window.confirm(`Delete facilitator ${f.name}? They will be unassigned from any batches.`)) return;
    try {
      await api.delete(`/admin/facilitators/${f.id}`);
      toast.success("Facilitator deleted");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-10">
        <Link to="/admin/companies" className="inline-flex items-center text-sm text-stone-500 hover:text-stone-800 mb-5">
          <ArrowLeft className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Admin
        </Link>

        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs tracking-[0.18em] uppercase text-stone-500 mb-2 flex items-center gap-1.5">
              <UserCog className="w-3.5 h-3.5" strokeWidth={1.75} /> Team
            </div>
            <h1 className="font-display text-3xl md:text-4xl text-stone-900 tracking-tight">Facilitators</h1>
            <p className="text-sm text-stone-500 mt-1.5">Create and manage facilitator accounts for your programs.</p>
          </div>
          <Button data-testid="create-facilitator-btn" onClick={() => setOpen(true)} className="bg-[#b81f24] hover:bg-[#9a1a1f] text-white rounded-md h-10 px-5">
            <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Create facilitator
          </Button>
        </div>

        {loading ? (
          <div className="text-stone-500 text-sm">Loading…</div>
        ) : facilitators.length === 0 ? (
          <div className="bg-white border border-dashed border-stone-200 rounded-3xl p-12 text-center">
            <UserCog className="w-10 h-10 text-stone-300 mx-auto mb-3" strokeWidth={1.25} />
            <div className="font-display text-lg text-stone-900">No facilitators yet</div>
            <p className="text-stone-500 text-sm mt-1">Create one to assign them to batches.</p>
          </div>
        ) : (
          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="facilitators-table">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-500 text-[10px] tracking-[0.18em] uppercase">
                <tr>
                  <th className="text-left px-5 py-3">Name</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Created</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {facilitators.map((f) => (
                  <tr key={f.id} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`facilitator-${f.id}`}>
                    <td className="px-5 py-3 font-medium text-stone-900">
                      <span className="inline-flex items-center gap-2">
                        {f.name}
                        {f.must_change_password && (
                          <span data-testid={`pending-badge-${f.id}`} className="text-[9px] tracking-[0.12em] uppercase px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">Pending</span>
                        )}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-stone-600">{f.email}</td>
                    <td className="px-5 py-3 text-stone-500">{fmt(f.created_at)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          data-testid={`reset-facilitator-${f.id}-btn`}
                          variant="outline"
                          onClick={() => resetPassword(f)}
                          className="rounded-md h-8 border-stone-300 text-stone-700"
                        >
                          <KeyRound className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} /> Reset password
                        </Button>
                        <Button
                          data-testid={`delete-facilitator-${f.id}-btn`}
                          variant="outline"
                          onClick={() => deleteFacilitator(f)}
                          className="rounded-md h-8 border-red-200 text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} /> Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CREATE FACILITATOR DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="font-display">Create facilitator</DialogTitle></DialogHeader>
          <form onSubmit={createFacilitator} className="space-y-4 mt-2" data-testid="create-facilitator-form">
            <div>
              <Label className="text-xs text-stone-600 mb-1.5 block">Name *</Label>
              <Input data-testid="facilitator-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Priya Sharma" className="h-10" />
            </div>
            <div>
              <Label className="text-xs text-stone-600 mb-1.5 block">Email *</Label>
              <Input data-testid="facilitator-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="facilitator@company.com" className="h-10" />
            </div>
            <div>
              <Label className="text-xs text-stone-600 mb-1.5 block">Password</Label>
              <Input data-testid="facilitator-password-input" type="text" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Leave blank to auto-generate" className="h-10" />
              <p className="text-[11px] text-stone-400 mt-1">Leave blank to auto-generate a temporary password. They&apos;ll change it on first login.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="save-facilitator-btn" type="submit" disabled={saving} className="bg-[#b81f24] hover:bg-[#9a1a1f] text-white">
                {saving ? "Creating…" : "Create facilitator"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
